"use client";

import { useState, useEffect } from "react";
import { type IProduct } from '@/lib/types/productType';

export interface SavedItem {
  id: string;
  productId: number;
  productTitle: string;
  productImage: string;
  price: number;
  condition: string;
  sellerId: number;
  sellerName: string;
  savedAt: Date;
  quantity?: number;
}

// Save for Later storage key for localStorage
const SAVE_FOR_LATER_STORAGE_KEY = "retro-stall-save-for-later";

// Utility function to generate unique saved item ID
const generateSavedItemId = () => `saved-item-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

// Save for Later Store Class
class SaveForLaterStore {
  private items: SavedItem[] = [];
  private listeners: Set<() => void> = new Set();

  constructor() {
    this.loadItemsFromStorage();
  }

  // Load items from localStorage
  private loadItemsFromStorage() {
    if (typeof window === 'undefined') return;

    try {
      const storedItems = localStorage.getItem(SAVE_FOR_LATER_STORAGE_KEY);
      if (storedItems) {
        const parsedItems = JSON.parse(storedItems) as Partial<SavedItem>[];
        // Convert date strings back to Date objects
        this.items = parsedItems.map((item: unknown) => {
          const savedItem = item as Record<string, unknown>;
          return {
            ...savedItem,
            savedAt: new Date(savedItem.savedAt as string)
          } as SavedItem;
        });
      }
    } catch (error) {
      console.error('Error loading saved items from storage:', error);
      this.items = [];
    }
  }

  // Save items to localStorage
  private saveItemsToStorage() {
    if (typeof window === 'undefined') return;

    try {
      localStorage.setItem(SAVE_FOR_LATER_STORAGE_KEY, JSON.stringify(this.items));
    } catch (error) {
      console.error('Error saving items to storage:', error);
    }
  }

  // Notify all listeners of changes
  private notifyListeners() {
    console.log('💾 SaveForLaterStore notifying listeners, items:', this.items.length);
    this.listeners.forEach(listener => listener());
  }

  // Subscribe to changes
  subscribe(listener: () => void) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  // Get current items
  getItems(): SavedItem[] {
    return [...this.items];
  }

  // Add item to saved list
  addItem(product: IProduct, quantity: number = 1): SavedItem | null {
    console.log('💾 SaveForLaterStore.addItem called with:', { productId: product.id, quantity });

    // Check if item already exists
    const existingItemIndex = this.items.findIndex(item => item.productId === product.id);

    if (existingItemIndex >= 0) {
      // Item already saved, don't add duplicate
      console.log('💾 Item already saved:', product.id);
      return null;
    }

    const newItem: SavedItem = {
      id: generateSavedItemId(),
      productId: product.id,
      productTitle: product.title,
      productImage: product.image,
      price: parseFloat(product.price.replace('€', '')),
      condition: product.condition,
      sellerId: product.stall.id,
      sellerName: product.stallName,
      savedAt: new Date(),
      quantity
    };

    this.items.push(newItem);
    this.saveItemsToStorage();
    this.notifyListeners();

    return newItem;
  }

  // Remove item from saved list
  removeItem(itemId: string): boolean {
    const itemIndex = this.items.findIndex(item => item.id === itemId);
    if (itemIndex === -1) return false;

    this.items.splice(itemIndex, 1);
    this.saveItemsToStorage();
    this.notifyListeners();

    return true;
  }

  // Clear all saved items
  clearItems() {
    this.items = [];
    this.saveItemsToStorage();
    this.notifyListeners();
  }

  // Check if item is saved
  isItemSaved(productId: number): boolean {
    return this.items.some(item => item.productId === productId);
  }

  // Get total number of saved items
  getTotalItems(): number {
    return this.items.length;
  }

  // Move item to cart and remove from saved list
  moveToCart(itemId: string): IProduct | null {
    const item = this.items.find(item => item.id === itemId);

    if (item) {
      // Remove from saved items
      this.removeItem(itemId);

      // Return product data for cart
      return {
        id: item.productId,
        title: item.productTitle,
        image: item.productImage,
        images: [item.productImage],
        price: item.price.toString(),
        condition: item.condition,
        stall: {
          id: item.sellerId,
          name: item.sellerName,
          description: '',
          products: []
        },
        category: { id: '', name: '' },
        rating: 0,
        reviewCount: 0,
        stallName: item.sellerName,
        stallId: item.sellerId,
        isFeatured: false,
        description: ''
      } as IProduct;
    }
    return null;
  }

  // Get items grouped by seller
  getItemsBySeller(): Record<string, SavedItem[]> {
    return this.items.reduce((acc, item) => {
      if (!acc[item.sellerId]) {
        acc[item.sellerId] = [];
      }
      acc[item.sellerId].push(item);
      return acc;
    }, {} as Record<string, SavedItem[]>);
  }
}

// Create singleton instance
export const saveForLaterStore = new SaveForLaterStore();

// Export utility functions for components
export const useSaveForLater = () => {
  const [, forceUpdate] = useState(0);

  useEffect(() => {
    const unsubscribe = saveForLaterStore.subscribe(() => {
      forceUpdate((prev: number) => prev + 1);
    });

    return () => {
      unsubscribe();
    };
  }, []);

  return {
    items: saveForLaterStore.getItems(),
    addItem: saveForLaterStore.addItem.bind(saveForLaterStore),
    removeItem: saveForLaterStore.removeItem.bind(saveForLaterStore),
    clearItems: saveForLaterStore.clearItems.bind(saveForLaterStore),
    isItemSaved: saveForLaterStore.isItemSaved.bind(saveForLaterStore),
    getTotalItems: saveForLaterStore.getTotalItems.bind(saveForLaterStore),
    moveToCart: saveForLaterStore.moveToCart.bind(saveForLaterStore),
    getItemsBySeller: saveForLaterStore.getItemsBySeller.bind(saveForLaterStore),
    subscribe: saveForLaterStore.subscribe.bind(saveForLaterStore)
  };
};
